import { Grid2, TextField, Typography } from '@mui/material'
import { AmlAnswerIn } from 'api/compliance'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { verificationOtherPath, verifyingPath } from 'components/Routes'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import { FormikContextType, useFormikContext } from 'formik'
import {
  useAmlQuestions,
  useMutateAmlAnswers,
  useMutateStartDiditVerification
} from 'hooks/useCompliance'
import { useUserProfile } from 'hooks/useUserProfile'
import { forwardRef, useImperativeHandle } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormValues } from '../'
import {
  commentsRequired,
  isMultiSelect,
  isSingleSelect
} from './amlOptionUtils'
import MultiSelectQuestion from './MultiSelectQuestion'
import SingleSelectQuestion from './SingleSelectQuestion'

interface FinancialDetailsFormRef {
  handleClickVerify: () => void
}

const FinancialDetailsForm = forwardRef<FinancialDetailsFormRef>(
  (_, ref): JSX.Element => {
    const { setToast } = useAuth()
    const navigate = useNavigate()

    const { state } = useLocation()

    const {
      isSubmitting,
      submitForm,
      values,
      errors,
      setFieldValue
    }: FormikContextType<FormValues> = useFormikContext()

    const { data: amlData, isLoading: amlLoading } = useAmlQuestions()
    const { data: profileData } = useUserProfile()

    const {
      mutateAsync: answerAmlQuestions,
      isLoading: answerAmlQuestionsLoading
    } = useMutateAmlAnswers()

    const {
      mutateAsync: startDiditVerification,
      isLoading: requestVerificationLoading
    } = useMutateStartDiditVerification()

    useImperativeHandle(ref, () => ({
      handleClickVerify() {
        if (Object.keys(errors).length === 0) {
          const answers = buildAnswerInput()
          answerAmlQuestions(answers)
            .then(() => {
              const verificationMethod = (
                state as { verificationMethod?: string }
              )?.verificationMethod
              if (verificationMethod === 'other') {
                return navigate(verificationOtherPath())
              }
              if (!profileData) {
                setToast({
                  open: true,
                  type: 'error',
                  duration: 3000,
                  message: 'Profile not loaded'
                })
                return
              }
              // Didit hosted IDV (document + biometric/liveness). The backend resolves the
              // profile itself; we pass the URL Didit returns the user to, where the
              // /verifying page polls for the async webhook decision.
              const callbackUrl = `${window.location.origin}${verifyingPath()}`
              startDiditVerification(callbackUrl)
                .then(({ url }) => {
                  window.location.assign(url)
                })
                .catch(error => {
                  setToast({
                    open: true,
                    type: 'error',
                    duration: 3000,
                    message:
                      error instanceof Error
                        ? error.message
                        : 'Verification request failed'
                  })
                })
            })
            .catch(error => {
              setToast({
                open: true,
                type: 'error',
                duration: 3000,
                message:
                  error instanceof Error
                    ? error.message
                    : 'Failed to save AML answers'
              })
            })
        }
      }
    }))

    function buildAnswerInput(): AmlAnswerIn[] {
      if (!amlData?.questions) return []
      return amlData.questions.map(question => {
        const answer: AmlAnswerIn = {
          key: question.key,
          answer: String(values[question.key] ?? '')
        }
        // When a chosen option demands elaboration (comments_required, e.g.
        // source_of_funds 'Other'), the backend rejects the submission
        // without a comments value — include it for that question key.
        if (commentsRequired(question, values[question.key])) {
          answer.comments = String(values.comments?.[question.key] ?? '')
        }
        return answer
      })
    }

    return (
      <>
        <Typography my={2.5} variant='h6'>
          &emsp;Part of our anti-money laundering obligations includes
          understanding where your investment income comes from and how you
          intend to use Goldie.
        </Typography>
        <LoadingWrapper loading={amlLoading}>
          <Grid2 container spacing={2}>
            {amlData?.questions?.map(question => (
              <Grid2 key={question.key} size={{ xs: 12 }}>
                <Typography variant='body1' mb={1}>
                  {question.label}
                </Typography>
                {isSingleSelect(question) ? (
                  <SingleSelectQuestion question={question} />
                ) : isMultiSelect(question) ? (
                  <MultiSelectQuestion question={question} />
                ) : (
                  // free_text questions — and any question from an older
                  // backend that doesn't serve format/answer_options
                  <TextField
                    fullWidth
                    name={question.key}
                    value={values[question.key] ?? ''}
                    onChange={e => setFieldValue(question.key, e.target.value)}
                    error={!!errors[question.key]}
                    helperText={errors[question.key] as string}
                  />
                )}
              </Grid2>
            ))}
          </Grid2>
        </LoadingWrapper>
        <Grid2 mt={8} mb={6}>
          <Button
            color='primary'
            disabled={
              isSubmitting ||
              requestVerificationLoading ||
              answerAmlQuestionsLoading
            }
            fullWidth
            onClick={submitForm}
            type='button'
          >
            finish
          </Button>
        </Grid2>
      </>
    )
  }
)

export default FinancialDetailsForm
